import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

// Interface for comment data
interface CommentData {
  postId?: string;
  eventId?: string;
  imageId?: string;
  text: string;
  userId: string;
  timestamp: any;
  isDeleted?: boolean;
  type?: 'comment' | 'reaction';
  parentId?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class CommentsService {
  constructor(private firestore: AngularFirestore) {}

  /**
   * Get the comment count for a specific post/item
   * @param postId The ID of the post or item
   * @param itemType The type of the item (default: 'posts')
   * @returns Observable with the number of active comments (excluding deleted comments and reactions)
   */
  getCommentCount(
    postId: string,
    itemType: string = 'posts'
  ): Observable<number> {
    if (!postId) {
      return of(0);
    }

    // Determine the correct field name based on item type
    let fieldName = 'postId';
    if (itemType === 'events') {
      fieldName = 'eventId';
    } else if (itemType === 'event_images' || itemType === 'post_images') {
      fieldName = 'imageId';
    }

    return this.firestore
      .collection('comments', ref => {
        let query = ref.where(fieldName, '==', postId);
        // Don't filter by type or isDeleted initially
        return query;
      })
      .snapshotChanges()
      .pipe(
        map(actions => {
          if (!actions || actions.length === 0) {
            return 0;
          }
          // Filter in memory to handle both old and new comments
          const validComments = actions.filter(action => {
            const data = action.payload.doc.data() as CommentData;
            // Include comment if:
            // 1. It's not explicitly marked as deleted AND
            // 2. Either it has no type field (old comment) OR it's of type 'comment'
            return (
              !data.isDeleted &&
              (!data.type || data.type === 'comment') &&
              data.text &&
              data.text.trim() !== ''
            );
          });
          return validComments.length;
        }),
        catchError(error => {
          console.error('Error getting comment count:', error);
          return of(0);
        })
      );
  }

  /**
   * Migrate existing comments by adding missing fields
   * This should be called once to update all existing comments
   */
  async migrateExistingComments() {
    try {
      // Get all comments that don't have the type field
      const snapshot = await this.firestore
        .collection('comments')
        .ref.where('type', '==', null)
        .get();

      const batch = this.firestore.firestore.batch();
      let count = 0;

      snapshot.docs.forEach(doc => {
        const ref = this.firestore.collection('comments').doc(doc.id).ref;
        batch.update(ref, {
          type: 'comment',
          isDeleted: false,
        });
        count++;
      });

      if (count > 0) {
        await batch.commit();
        console.log(`Successfully migrated ${count} comments`);
      }

      // Get all comments that don't have isDeleted field
      const snapshot2 = await this.firestore
        .collection('comments')
        .ref.where('isDeleted', '==', null)
        .get();

      const batch2 = this.firestore.firestore.batch();
      let count2 = 0;

      snapshot2.docs.forEach(doc => {
        const ref = this.firestore.collection('comments').doc(doc.id).ref;
        batch2.update(ref, {
          isDeleted: false,
        });
        count2++;
      });

      if (count2 > 0) {
        await batch2.commit();
        console.log(
          `Successfully updated isDeleted field for ${count2} comments`
        );
      }

      return count + count2;
    } catch (error) {
      console.error('Error migrating comments:', error);
      throw error;
    }
  }
}

